import { NotificationsActive, Settings } from "@mui/icons-material";
import { Button, CircularProgress, Grid, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t } from "ttag";
import {
  IDeviceHealthFilters,
  IDeviceHealthRecord,
  IEntityRef,
  IHealthSubscription,
  INewSubscription,
  ISelectedFilters,
  ISiteNameIndex
} from "../../models/DeviceHealth.types";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import ServiceNavigationBar from "../../widgets/Menu/ServiceNavigationBar";
import DeviceHealthFilterGroup from "./DeviceHealthFilterGroup";
import DeviceHealthTable from "./DeviceHealthTable";
import DeviceRecordPopup from "./DeviceRecordPopup";
import ManageSubscriptionsPopup from "./ManageSubscriptionsPopup";
import RegisterSiteUpdatesPopup from "./RegisterSiteUpdatesPopup";
import useStyles from "./DeviceHealth.style";

const DEFAULT_PAGE_SIZE = 25;

/** Matches the commercial Audits page: filter edits settle before refetching. */
const FILTER_DEBOUNCE_MS = 600;

const emptyFilters: IDeviceHealthFilters = {
  reportDate: null,
  salesTerritories: [],
  customers: [],
  sites: []
};

/** Selections are explicit, so the screen opens with everything selected. */
const selectAll = (filters: IDeviceHealthFilters): ISelectedFilters => ({
  salesTerritories: filters.salesTerritories.map(({ id }) => id),
  customers: filters.customers.map(({ id }) => id),
  sites: filters.sites.map(({ id }) => id)
});

const DeviceHealth: React.FC<any> = (props: any) => {
  const classes: any = useStyles();

  const getFilters = useStoreActions((actions) => actions.deviceHealth.getFilters);
  const getReport = useStoreActions((actions) => actions.deviceHealth.getReport);
  const getSubscriptions = useStoreActions((actions) => actions.deviceHealth.getSubscriptions);
  const getSiteNameIndex = useStoreActions((actions) => actions.deviceHealth.getSiteNameIndex);
  const getAllCustomers = useStoreActions((actions) => actions.deviceHealth.getAllCustomers);
  const getCustomerSites = useStoreActions((actions) => actions.deviceHealth.getCustomerSites);
  const createSubscriptions = useStoreActions((actions) => actions.deviceHealth.createSubscriptions);
  const deleteSubscription = useStoreActions((actions) => actions.deviceHealth.deleteSubscription);
  const { addMessage } = useStoreActions((actions) => actions.errorMessage);

  const [filterOptions, setFilterOptions] = useState<IDeviceHealthFilters>(emptyFilters);
  // null until the filter options have loaded and been seeded, which is also what
  // gates the first report fetch.
  const [selectedFilters, setSelectedFilters] = useState<ISelectedFilters | null>(null);
  const [initializing, setInitializing] = useState<boolean>(true);

  const [records, setRecords] = useState<IDeviceHealthRecord[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [loadingReport, setLoadingReport] = useState<boolean>(false);

  const [reportDate, setReportDate] = useState<string | null>(null);

  /**
   * territory enum value -> label. Read from the store rather than baked into the
   * filter options, so labels appear as soon as getServiceTypes() resolves instead
   * of racing it and leaving bare enum numbers on screen.
   */
  const territoryNames = useStoreState((state: any) => state.regionTypesMirror);

  /** The filter panels need the resolved territory labels too, not the raw values. */
  const namedFilterOptions = useMemo(
    () => ({
      ...filterOptions,
      salesTerritories: filterOptions.salesTerritories.map(({ id }) => ({
        id,
        name: territoryNames?.[id] || id
      }))
    }),
    [filterOptions, territoryNames]
  );

  const [selectedRecord, setSelectedRecord] = useState<IDeviceHealthRecord | null>(null);

  const [showRegisterPopup, setShowRegisterPopup] = useState<boolean>(false);
  const [savingSubscription, setSavingSubscription] = useState<boolean>(false);
  const [allCustomers, setAllCustomers] = useState<IEntityRef[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState<boolean>(false);

  const [showSubscriptionsPopup, setShowSubscriptionsPopup] = useState<boolean>(false);
  const [subscriptions, setSubscriptions] = useState<IHealthSubscription[]>([]);
  const [siteNames, setSiteNames] = useState<ISiteNameIndex>({});
  const [loadingSubscriptions, setLoadingSubscriptions] = useState<boolean>(false);
  const [deletingSubscription, setDeletingSubscription] = useState<boolean>(false);

  // Guards against an earlier, slower request overwriting a newer one.
  const requestIdRef = useRef<number>(0);
  // The first load shouldn't sit behind the filter debounce.
  const hasLoadedOnceRef = useRef<boolean>(false);
  // Mirrors reportDate so fetchReport can read it without being re-created.
  const reportDateRef = useRef<string | null>(null);
  // Same, for how many options each panel offers.
  const optionCountsRef = useRef({ salesTerritories: 0, customers: 0, sites: 0 });

  const fetchReport = useCallback(
    async (filters: ISelectedFilters, nextPage: number, nextPageSize: number) => {
      requestIdRef.current++;
      const requestId = requestIdRef.current;

      setLoadingReport(true);

      try {
        const result = await getReport({
          filters,
          optionCounts: optionCountsRef.current,
          page: nextPage,
          pageSize: nextPageSize,
          reportDate: reportDateRef.current
        });

        if (requestId !== requestIdRef.current) {
          return;
        }

        setRecords(result?.records || []);
        setTotalCount(result?.totalCount || 0);

        if (result?.reportDate) {
          reportDateRef.current = result.reportDate;
          setReportDate(result.reportDate);
        }
      } catch (error: any) {
        if (requestId !== requestIdRef.current) {
          return;
        }

        addMessage({ message: error?.message || t`Failed to load device health data.` });
        setRecords([]);
        setTotalCount(0);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoadingReport(false);
        }
      }
    },
    [getReport, addMessage]
  );

  useEffect(() => {
    const initialize = async () => {
      try {
        const options = (await getFilters()) || emptyFilters;
        setFilterOptions(options);
        optionCountsRef.current = {
          salesTerritories: options.salesTerritories.length,
          customers: options.customers.length,
          sites: options.sites.length
        };
        reportDateRef.current = options.reportDate;
        setReportDate(options.reportDate);
        setSelectedFilters(selectAll(options));
      } catch (error: any) {
        addMessage({ message: error?.message || t`Failed to load device health filters.` });
      } finally {
        setInitializing(false);
      }
    };

    initialize();
  }, [getFilters, addMessage]);

  // A filter change always returns to the first page, debounced so rapid
  // checkbox edits result in a single request. Seeding the selection after the
  // options load is what triggers the very first fetch.
  useEffect(() => {
    if (!selectedFilters) {
      return;
    }

    const delay = hasLoadedOnceRef.current ? FILTER_DEBOUNCE_MS : 0;
    hasLoadedOnceRef.current = true;

    const timer = setTimeout(() => {
      setPage(0);
      fetchReport(selectedFilters, 0, pageSize);
    }, delay);

    return () => clearTimeout(timer);
  }, [selectedFilters]);

  const handleFiltersChange = (filters: ISelectedFilters) => {
    setSelectedFilters(filters);
  };

  const handlePageChange = (nextPage: number) => {
    if (!selectedFilters) {
      return;
    }

    setPage(nextPage);
    fetchReport(selectedFilters, nextPage, pageSize);
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    if (!selectedFilters) {
      return;
    }

    setPageSize(nextPageSize);
    setPage(0);
    fetchReport(selectedFilters, 0, nextPageSize);
  };

  const handleViewRecord = (record: IDeviceHealthRecord) => {
    setSelectedRecord(record);
  };

  const handleCloseRecordPopup = () => {
    setSelectedRecord(null);
  };

  const handleOpenRegisterPopup = async () => {
    setShowRegisterPopup(true);

    // Subscribing must be possible for any customer, not only those with rows in
    // today's report - so the list comes from the customers endpoint, on demand.
    if (allCustomers.length) {
      return;
    }

    setLoadingCustomers(true);

    try {
      setAllCustomers(await getAllCustomers());
    } catch (error: any) {
      addMessage({ message: error?.message || t`Failed to load customers.` });
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleCloseRegisterPopup = () => {
    setShowRegisterPopup(false);
  };

  const handleCreateSubscription = async (subscription: INewSubscription) => {
    setSavingSubscription(true);

    try {
      const result = await createSubscriptions(subscription);
      setShowRegisterPopup(false);

      // One row is created per (site, email); duplicates are skipped server-side,
      // so report both counts rather than a bare "saved".
      const created = result?.created || 0;
      const skipped = result?.skipped || 0;
      const createdText =
        created === 1 ? t`1 subscription created.` : t`${created} subscriptions created.`;
      const skippedText = skipped === 1 ? t`1 already existed.` : t`${skipped} already existed.`;

      addMessage({ message: skipped ? `${createdText} ${skippedText}` : createdText });
    } catch (error: any) {
      addMessage({ message: error?.message || t`Failed to create the subscription.` });
    } finally {
      setSavingSubscription(false);
    }
  };

  const fetchSubscriptions = async () => {
    setLoadingSubscriptions(true);

    try {
      // Site names come from the full site list, not the report filters - a
      // subscription can point at a site that has no rows in today's snapshot.
      // Fetched alongside the subscriptions, and only when the popup opens.
      const [result, names] = await Promise.all([getSubscriptions(), getSiteNameIndex()]);
      setSubscriptions(result || []);
      setSiteNames(names || {});
    } catch (error: any) {
      addMessage({ message: error?.message || t`Failed to load subscriptions.` });
      setSubscriptions([]);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const handleOpenSubscriptionsPopup = () => {
    setShowSubscriptionsPopup(true);
    fetchSubscriptions();
  };

  const handleCloseSubscriptionsPopup = () => {
    setShowSubscriptionsPopup(false);
  };

  const handleDeleteSubscription = async (subscriptionId: string) => {
    setDeletingSubscription(true);

    try {
      await deleteSubscription(subscriptionId);
      await fetchSubscriptions();
    } catch (error: any) {
      addMessage({ message: error?.message || t`Failed to delete the subscription.` });
    } finally {
      setDeletingSubscription(false);
    }
  };

  /**
   * With no filter options at all there is no snapshot for this date, which is a
   * different situation from a selection that happens to match nothing.
   */
  const getTableEmptyText = () => {
    const hasAnyOptions =
      !!filterOptions.salesTerritories.length ||
      !!filterOptions.customers.length ||
      !!filterOptions.sites.length;

    if (hasAnyOptions) {
      return t`No devices match the selected filters.`;
    }

    if (reportDate) {
      return t`No device health report has been generated for ${reportDate} yet.`;
    }

    return t`No device health report is available yet.`;
  };

  const renderReportDate = () => {
    if (!reportDate) {
      return null;
    }

    return (
      <Typography className={classes.reportDate}>
        {t`Daily snapshot for`} <strong>{reportDate}</strong>
      </Typography>
    );
  };

  const renderContent = () => {
    if (initializing || !selectedFilters) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      );
    }

    return (
      <>
        <div className={classes.actionsRow}>
          <Button
            variant="contained"
            startIcon={<NotificationsActive />}
            className={classes.actionButton}
            onClick={handleOpenRegisterPopup}
          >
            {t`Register for site updates`}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            className={classes.actionButton}
            onClick={handleOpenSubscriptionsPopup}
          >
            {t`Manage notification subscriptions`}
          </Button>
          {renderReportDate()}
        </div>

        <Grid container className={classes.contentRow}>
          <DeviceHealthFilterGroup
            filters={namedFilterOptions}
            selected={selectedFilters}
            onChange={handleFiltersChange}
          />
          <Grid item className={classes.tableWrapper}>
            <DeviceHealthTable
              records={records}
              territoryNames={territoryNames}
              emptyText={getTableEmptyText()}
              totalCount={totalCount}
              page={page}
              pageSize={pageSize}
              loading={loadingReport}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onViewRecord={handleViewRecord}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <ServiceNavigationBar title={t`Device Health`} {...props}>
      <div className={classes.view}>{renderContent()}</div>

      {!!selectedRecord && (
        <DeviceRecordPopup
          record={selectedRecord}
          territoryName={
            selectedRecord.salesTerritory === undefined || selectedRecord.salesTerritory === null
              ? "-"
              : territoryNames[String(selectedRecord.salesTerritory)] ||
                String(selectedRecord.salesTerritory)
          }
          onClose={handleCloseRecordPopup}
        />
      )}

      {showRegisterPopup && (
        <RegisterSiteUpdatesPopup
          customers={allCustomers}
          loadingCustomers={loadingCustomers}
          loadCustomerSites={getCustomerSites}
          saving={savingSubscription}
          onClose={handleCloseRegisterPopup}
          onSubmit={handleCreateSubscription}
        />
      )}

      {showSubscriptionsPopup && (
        <ManageSubscriptionsPopup
          subscriptions={subscriptions}
          siteNames={siteNames}
          loading={loadingSubscriptions}
          deleting={deletingSubscription}
          onClose={handleCloseSubscriptionsPopup}
          onDelete={handleDeleteSubscription}
        />
      )}
    </ServiceNavigationBar>
  );
};

export default DeviceHealth;
